import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { revealTransition } from '../revealTransition';

/* ---------- Mini live previews ---------- */

const EpochPreview = () => {
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    const i = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <div className="font-mono text-[11px] text-center">
      <div className="text-blue-500 font-semibold tabular-nums text-sm">{now}</div>
      <div className="text-gray-400 leading-none">↓</div>
      <div className="text-gray-600">{new Date(now * 1000).toUTCString().slice(5, 25)}</div>
    </div>
  );
};

const JSONPreview = () => (
  <div className="pv-stage font-mono text-[10px]">
    <div className="pv-a text-gray-600">{'{"name":"dev","tags":["js","api"]}'}</div>
    <div className="pv-b">
      <pre className="text-left leading-tight text-gray-600">{'{\n  '}<span className="text-green-600">"name"</span>{': "dev",\n  '}<span className="text-green-600">"tags"</span>{': ["js", "api"]\n}'}</pre>
    </div>
  </div>
);

const YAMLPreview = () => (
  <pre className="font-mono text-[10px] text-left leading-relaxed">
    <div className="pv-line" style={{ animationDelay: '0ms' }}><span className="text-orange-500">server</span><span className="text-gray-500">:</span></div>
    <div className="pv-line" style={{ animationDelay: '250ms' }}>{'  '}<span className="text-orange-500">host</span><span className="text-gray-500">: localhost</span></div>
    <div className="pv-line" style={{ animationDelay: '500ms' }}>{'  '}<span className="text-orange-500">port</span><span className="text-gray-500">: </span><span className="text-blue-500">8080</span></div>
  </pre>
);

const MarkdownPreview = () => (
  <div className="pv-stage text-[11px]">
    <div className="pv-a font-mono text-gray-600">## Hello **world**</div>
    <div className="pv-b">
      <div className="text-gray-800"><span className="text-base font-bold">Hello <em className="text-teal-600 not-italic font-extrabold">world</em></span></div>
    </div>
  </div>
);

const TextDiffPreview = () => (
  <pre className="font-mono text-[10px] text-left leading-relaxed">
    <div className="text-gray-500">{'  total = price'}</div>
    <div><span className="pv-del text-red-600">{'- price'}</span></div>
    <div><span className="pv-add text-green-600">{'+ price * qty'}</span></div>
  </pre>
);

const TextFormatterPreview = () => (
  <div className="pv-stage font-mono text-[11px]">
    <div className="pv-a text-gray-500">{'"  MESSY    text   "'}</div>
    <div className="pv-b text-pink-600 font-semibold">"Messy Text"</div>
  </div>
);

const JSONDiffPreview = () => (
  <pre className="font-mono text-[10px] text-left leading-relaxed text-gray-600">
    <div>{'"version": '}<span className="pv-del text-red-600">1</span></div>
    <div>{'"version": '}<span className="pv-add pv-chip text-green-700">2</span></div>
    <div>{'"stable": '}<span className="text-blue-500">true</span></div>
  </pre>
);

const JWTPreview = () => (
  <div className="font-mono text-[11px] break-all text-center leading-relaxed">
    <span className="pv-pulse text-red-500" style={{ animationDelay: '0ms' }}>eyJhbGc</span>
    <span className="text-gray-400">.</span>
    <span className="pv-pulse text-purple-500" style={{ animationDelay: '400ms' }}>eyJzdWIi</span>
    <span className="text-gray-400">.</span>
    <span className="pv-pulse text-blue-500" style={{ animationDelay: '800ms' }}>SflKxwRJ</span>
  </div>
);

const Base64Preview = () => (
  <div className="pv-stage font-mono text-[11px]">
    <div className="pv-a text-gray-600">Hello World</div>
    <div className="pv-b text-slate-500">SGVsbG8gV29ybGQ=</div>
  </div>
);

/* ---------- Tool definitions ---------- */

const ICON_PATHS = {
  epoch: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  json: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  yaml: 'M4 6h16M4 12h16M4 18h7',
  markdown: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2zM9 8h1',
  textdiff: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  textformat: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  jsondiff: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  jwt: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  base64: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
};

const TOOLS = [
  { name: 'Epoch Converter', description: 'Convert between Unix timestamps and human-readable time', path: '/epoch-converter', icon: 'epoch', accent: '#3b82f6', preview: <EpochPreview /> },
  { name: 'JSON Formatter', description: 'Format, validate, and transform JSON data', path: '/json-formatter', icon: 'json', accent: '#22c55e', preview: <JSONPreview /> },
  { name: 'YAML Formatter', description: 'Format, validate, and visualize YAML data with tree view', path: '/yaml-formatter', icon: 'yaml', accent: '#f97316', preview: <YAMLPreview /> },
  { name: 'Markdown Formatter', description: 'Live preview and beautify Markdown documents', path: '/markdown-formatter', icon: 'markdown', accent: '#14b8a6', preview: <MarkdownPreview /> },
  { name: 'Text Diff', description: 'Compare two text blocks and highlight differences', path: '/text-diff', icon: 'textdiff', accent: '#6366f1', preview: <TextDiffPreview /> },
  { name: 'Text Formatter', description: 'Clean and format text content', path: '/text-formatter', icon: 'textformat', accent: '#ec4899', preview: <TextFormatterPreview /> },
  { name: 'JSON Diff', description: 'Compare two JSON objects and highlight differences', path: '/json-diff', icon: 'jsondiff', accent: '#eab308', preview: <JSONDiffPreview /> },
  { name: 'JWT Tool', description: 'Encode, decode and validate JSON Web Tokens', path: '/jwt-tool', icon: 'jwt', accent: '#a855f7', preview: <JWTPreview /> },
  { name: 'Base64 Tool', description: 'Encode and decode Base64 strings', path: '/base64-tool', icon: 'base64', accent: '#64748b', preview: <Base64Preview /> },
];

/* ---------- Spotlight + tilt card ---------- */

const ToolCard = ({ tool, index }) => {
  const navigate = useNavigate();

  const onClick = (e) => {
    e.preventDefault();
    revealTransition(e.clientX, e.clientY, () => navigate(tool.path));
  };

  const onMove = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    el.style.setProperty('--mx', `${x}px`);
    el.style.setProperty('--my', `${y}px`);
    const rx = ((y - r.height / 2) / r.height) * -5;
    const ry = ((x - r.width / 2) / r.width) * 5;
    el.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
  };
  const onLeave = (e) => {
    e.currentTarget.style.transform = '';
  };

  const slug = tool.path.replace('/', '');
  return (
    <Link to={tool.path} onClick={onClick} className="block" data-testid={`tool-card-${slug}`}>
      <div
        className="tool-card tool-card-enter relative bg-white rounded-xl p-5 w-80 h-52 overflow-hidden shadow-sm"
        style={{ '--accent': tool.accent, animationDelay: `${index * 70}ms` }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <div className="spot absolute inset-0 pointer-events-none" />
        <div className="relative h-full flex flex-col justify-center text-center">
          <div className="tc-flip relative h-20 mb-3">
            <div className="tc-icon absolute inset-0 flex items-center justify-center">
              <svg className="w-12 h-12" style={{ color: tool.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={ICON_PATHS[tool.icon]} />
              </svg>
            </div>
            <div className="tc-preview absolute inset-0 flex items-center justify-center px-3">
              {tool.preview}
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-1">{tool.name}</h3>
          <p className="text-gray-600 text-sm">{tool.description}</p>
        </div>
      </div>
    </Link>
  );
};

/* ---------- Home page ---------- */

export default function Home() {
  return (
    <div className="min-h-screen bg-white home-bg flex flex-col">
      <header className="py-12 px-4 tool-card-enter">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl font-light text-gray-800 mb-3">DevTools</h1>
          <p className="text-gray-600 text-lg">Professional developer utilities for everyday tasks</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 flex-1">
        <h2 className="text-2xl font-medium text-gray-800 text-center mb-8 tool-card-enter" style={{ animationDelay: '100ms' }}>
          Text &amp; Data Tools
        </h2>
        <div className="flex flex-wrap justify-center gap-6">
          {TOOLS.map((tool, i) => (
            <ToolCard key={tool.path} tool={tool} index={i} />
          ))}
        </div>
      </main>

      <footer className="py-8 text-center border-t border-gray-200 mt-12">
        <p className="text-gray-500 text-sm">Professional developer tools for productivity and efficiency</p>
      </footer>
    </div>
  );
}
